class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.isOpen = false;

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  normalizeQuery(value) {
    return value.toLowerCase().trim().split(/\s+/).filter(Boolean).join(' ');
  }

  isQueryBlocked(searchTerm) {
    const phrases = window.searchBlockedPhrases || [];
    return phrases.indexOf(this.normalizeQuery(searchTerm)) !== -1;
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) return;

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    if (this.isQueryBlocked(searchTerm)) {
      this.removeAttribute('loading');
      this.removeAttribute('results');
      this.removeAttribute('open');
      this.input.setAttribute('aria-expanded', 'false');
      this.predictiveSearchResults.removeAttribute('style');
      this.predictiveSearchResults.innerHTML = '';
      this.isOpen = false;
      return;
    }

    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection&${encodeURIComponent('resources[limit]')}=4&section_id=predictive-search`)
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.getElementById('shopify-section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.removeAttribute('open');
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }
}

customElements.define('predictive-search', PredictiveSearch);


class AutocompleteSearch extends HTMLElement {
  constructor() {
    super();
    this.inputContainerEl = this.querySelector('.input-container');
    this.textInputEl = this.querySelector("input.search__input");
    this.suggestionEl = this.querySelector(".suggestion-container");
    this.svgTabIcon = this.querySelector(".icon.tab-key");
    this.svgEnterIcon = this.querySelector(".icon.enter-key");

    this.ENTER_KEYCODE = 13;
    this.TAB_KEYCODE = 9;
    this.BACKSPACE_KEYCODE = 8;
    this.UP_ARROW_KEYCODE = 38;
    this.DOWN_ARROW_KEYCODE = 40;
    this.SPACE_KEYCODE = 32;

    this.suggestedWord = "";
    this.suggestedWordsArray = [];
    this.currentWordIndex = 0;
    this.insertText = false;

    this.wordsArray = [];
    this.cacheKeywords();
    this.setupEventListeners();
    this.removeClassAfterAnimationCompletes(this.inputContainerEl, "animate");
  }

  cacheKeywords() {
    var that = this;

    if (localStorage.searchkeyword) {
      var storedKeywords = JSON.parse(localStorage.searchkeyword);
      that.wordsArray = storedKeywords;
    } else { 
      fetch('/products.json?limit=500').then(function (response) {
        // The API call was successful!
        return response.json();
      }).then(function (data) {
        // This is the JSON from our response
        for (let i in data.products) {
          that.wordsArray.push(data.products[i].title.toUpperCase());
        }
        if (that.wordsArray.length > 0) {
          localStorage.setItem("searchkeyword", JSON.stringify(that.wordsArray));
        }

      }).catch(function (err) {
        // There was an error
        console.warn('Something went wrong.', err);
        that.wordsArray = [];
      });
    }
  }
  
  setupEventListeners() {
    this.textInputEl.addEventListener("input", e => {
      if (e.data != " ") {
        this.insertText = true;
      }
      if (this.insertText == false) {
        this.textInputEl.value = "";
      }

      let inputValue = e.target.value.toUpperCase();
      this.suggestedWordsArray = this.filterArray(this.wordsArray, inputValue);
      this.suggestedWord = this.suggestedWordsArray[0];

      if (this.suggestedWord != undefined) {
        this.suggestionEl.innerHTML = this.suggestedWord;
      }

      if (inputValue.length == 0 || this.suggestedWordsArray.length == 0) {
        this.suggestionEl.innerHTML = "";
      }

      if (this.suggestedWordsArray.length != 0) {
        this.svgTabIcon.classList.remove("hidden");
        this.svgEnterIcon.classList.add("hidden");
      } else {
        this.svgTabIcon.classList.add("hidden");
        this.svgEnterIcon.classList.remove("hidden");
      }

      if (inputValue.length == 0 || inputValue == this.suggestedWord) {
        this.svgTabIcon.classList.add("hidden");
        this.svgEnterIcon.classList.add("hidden");
      }

      if (this.textInputEl.value.length == 0) {
        this.insertText = false;
      }
    });

    this.textInputEl.addEventListener("keydown", e => {
      if (e.keyCode == this.ENTER_KEYCODE) {
        if (this.textInputEl.value.length == 0) return;
        let inputValue = this.textInputEl.value.toUpperCase();
        let words = inputValue.split(" ");
        for (let i in words) {
          if (words[i].length != 0) {
            wordsArray.push(words[i]);
            textInputEl.value = "";
            this.suggestionEl.innerHTML = "";
          }
        }
        wordsArray = this.removeDuplicatesFromArray(wordsArray);
        this.inputContainerEl.classList.add("animate");
        this.svgTabIcon.classList.add("hidden");
        this.svgEnterIcon.classList.add("hidden");
        this.removeClassAfterAnimationCompletes(this.inputContainerEl, "animate");
      }

      if (this.textInputEl.value.length != 0) {
        if (e.keyCode == this.UP_ARROW_KEYCODE) {
          if (this.currentWordIndex == 0) return;
          this.currentWordIndex--;
          this.suggestionEl.innerHTML = this.suggestedWordsArray[this.currentWordIndex];
        }

        if (e.keyCode == this.DOWN_ARROW_KEYCODE) {
          if (this.currentWordIndex == this.suggestedWordsArray.length - 1) return;
          this.currentWordIndex++;
          this.suggestionEl.innerHTML = this.suggestedWordsArray[this.currentWordIndex];
        }

        if (e.keyCode == this.BACKSPACE_KEYCODE) {
          this.currentWordIndex = 0;
        }
      }

      if (this.suggestedWord != undefined && this.suggestedWord != "") {
        if (e.keyCode == this.TAB_KEYCODE) {
          e.preventDefault();
          this.textInputEl.value = this.suggestedWordsArray[this.currentWordIndex];
          this.suggestionEl.innerHTML = "";
          this.svgTabIcon.classList.add("hidden");
          this.svgEnterIcon.classList.add("hidden");
        }
      }
    });
  }

  removeClassAfterAnimationCompletes(el, className) {
    let elStyles = window.getComputedStyle(this.inputContainerEl);
    setTimeout(function() {
      el.classList.remove(className);
    }, +elStyles.animationDuration.replace("s", "") * 1000);
  }

  filterArray(array, item, reverse = false) {
    if (reverse) {
      return array
        .filter(word => this.compareTwoStrings(word, item))
        .sort((a, b) => a.length - b.length);
    } else {
      return array
        .filter(word => this.compareTwoStrings(word, item))
        .sort((a, b) => b.length - a.length);
    }
  }

  removeDuplicatesFromArray(array) {
    return [...new Set(array.map(i => i))];
  }

  compareTwoStrings(string, subString) {
    let temp = string.split("", subString.length).join("");
    if (subString == temp) return subString;
  }

}

customElements.define('autocomplete-search', AutocompleteSearch);


class ViewAllSearch extends HTMLElement {
  constructor() {
    super();
    this.viewAllLink = this.querySelector('.view-all-link');
    this.productsListingContainer = this.querySelector('ul.grid');

    this.setupEventListeners();
  }
  
  setupEventListeners() {
    var that = this;
    this.viewAllLink.addEventListener('click', (event) => {
      that.viewAllLink.classList.add('hidden');
      that.productsListingContainer.classList.remove('hidden');
      that.productsListingContainer.classList.add('extra-products-visible');
    });
  }

}

customElements.define('view-all-search', ViewAllSearch);
